import {action, observable, computed} from 'mobx';
import {validateEmail, validatePhone, validateName} from '../utils/validators';
import {normalizePhoneNumber, prettyFormatPhoneNumber} from '../../common/utils';

export const defaultValidateState = {
  name: {
    valid: true,
    message: '',
  },
  email: {
    valid: true,
    message: '',
  },
  phone: {
    valid: true,
    message: '',
  },
};
export class GuestContactModel {
  // Contact Specific Fields
  contact_id = '';

  contact_group_ids = [];

  // Fields synced to server
  @observable accessor email = '';

  @observable accessor invite_method = 'email';

  @observable accessor name = '';

  @observable accessor phone = '';

  guest_id = '';

  invited_by = '';

  // Presentation Fields
  key = '';

  store = {};

  @observable accessor editState = {};

  @observable accessor editing = false;

  @observable accessor isGuest = false;

  @observable accessor isDraft = false;

  @observable accessor isImport = false;

  @observable accessor selected = false;

  @observable accessor validateEditFields = defaultValidateState;

  @observable accessor validateFields = defaultValidateState;

  // other
  searchStr = ''; // will contain lowercase name, email and phone for searching against user search text

  get editNameMessage() {
    return this.validateEditFields.name.message || '';
  }

  get editEmailMessage() {
    return this.validateEditFields.email.message || '';
  }

  get editPhoneMessage() {
    return this.validateEditFields.phone.message || '';
  }

  // Normalize data differences between Guest & Contact for the fields we need
  constructor(json, isContact = false, isDraft = false, isImport = false) {
    this.key = evite.guid();
    this.update(json, isContact, isDraft, isImport, true);
  }

  buildName = (json) => {
    let name = '';
    if (json.first_name || json.last_name) {
      name = `${json.first_name} ${json.last_name}`;
    } else if (json.email) {
      name = json.email.split('@')[0].replace(/^"+|"+$/g, '');
    } else {
      name = '';
    }
    return name;
  };

  @computed
  get prettyPhone() {
    return prettyFormatPhoneNumber(this.phone);
  }

  @computed
  get isValid() {
    return Object.keys(this.validateFields).reduce(
      (previous, current) => previous && this.validateFields[current].valid,
      true
    );
  }

  @computed
  get isEditValid() {
    return Object.keys(this.validateEditFields).reduce(
      (previous, current) => previous && this.validateEditFields[current].valid,
      true
    );
  }

  @action
  editGuest = () => {
    this.editState = {...this.toJson};
    this.editState.phone = this.prettyPhone;
    this.editing = true;
    return this;
  };

  @action
  cancelEdit = () => {
    this.editing = false;
    this.editState = {};
    return this;
  };

  @action
  saveChanges = () => {
    if (this.isEditValid) {
      if (this.editState.phone.length >= 10) {
        this.editState.phone = this.editState.phone.replace(/\D+/g, '');
        this.editState.phone = normalizePhoneNumber(this.editState.phone);
      }
      const saved = this.update(this.editState, this.isContact, this.isDraft, this.isImport);
      this.editing = false;
      this.editState = {};
      return saved;
    }
    return false;
  };

  @action
  validate = () => {
    this.validateFields = {...defaultValidateState};
    this.runValidator('name', validateName(this));
    if (this.invite_method === 'sms') {
      this.runValidator('phone', validatePhone(this.phone));
      // TODO: IS EMAIL REQUIRED FOR SMS???
      // this.runValidator('email', validateEmail(this.email, true));
    } else {
      this.runValidator('email', validateEmail(this.email));
    }
  };

  @action
  validateEditState = () => {
    this.validateEditFields = {...defaultValidateState};
    // EVT-10494 -- automatically select phone if email is empty
    if (this.editState.phone.length >= 10 && !this.editState.email.length) {
      this.editState.invite_method = 'sms';
    }
    this.runValidator('name', validateName(this.editState), true);
    if (this.editState.invite_method === 'sms') {
      this.runValidator('phone', validatePhone(this.editState.phone), true);
    } else {
      this.runValidator('email', validateEmail(this.editState.email), true);
    }
  };

  runValidator(field, {valid, message}, edit = false) {
    this.validateEditFields[field] = {
      valid,
      message: message || '',
    };
    if (!edit) {
      this.validateFields[field] = {
        valid,
        message: message || '',
      };
    }
  }

  @action
  addToGuestList = () => {
    if (!this.selected) {
      this.selected = true;
      this.isDraft = true;
    }
  };

  @action
  removeFromGuestList = () => {
    if (this.selected) {
      this.selected = false;
      this.isDraft = false;
    }
  };

  @action
  setInviteMethod(method) {
    if (['email', 'sms'].includes(method)) {
      this.invite_method = method;
    }
  }

  @action
  update(json, isContact = false, isDraft = false, isImport = false, skipValidation = false) {
    this.phone = json.phone ? json.phone : this.phone;
    this.selected = json.selected;
    this.phone_country_code = json.phone_country_code
      ? json.phone_country_code
      : this.phone_country_code;
    this.email = json.email ? json.email : this.email;
    this.guest_id = json.guest_id;
    this.invite_method = json.invite_method ? json.invite_method : this.invite_method;
    this.invited_by = json.invited_by;
    let {name} = json;

    if (!name) {
      name = this.buildName(json);
    }
    this.name = name;

    if (this.phone && !this.email) {
      this.invite_method = 'sms';
    }

    if (this.phone) this.phone = normalizePhoneNumber(this.phone);

    if (json.invited) {
      // this contact is on the sent guest list already
      this.isGuest = true;
      this.selected = true;
    } else if (isContact) {
      this.contact_group_ids = json.contact_group_ids;
      this.contact_id = json.id;
    } else if (isDraft) {
      this.isDraft = true;
      this.selected = true;
      this.contact_group_ids = json.contact_group_ids;
      this.contact_id = json.contact_id;
    } else if (isImport) {
      this.isImport = true;
    } else {
      this.isGuest = true;
      this.selected = true;
    }

    // set searchStr ahead of time for speedy interactive searching
    this.searchStr = `${this.name} ${this.email} ${this.phone}`.toLowerCase();
    if (!skipValidation) {
      this.validate();
    }
    return this;
  }

  matchesText = (textStr) =>
    // Note: textStr should be lowercase
    this.searchStr.includes(textStr);

  comparisonValueFor = (attributeName) => {
    if (attributeName == 'name') return this.name;

    if (attributeName == 'email') {
      // email really means "email or phone" - so whichever it has
      if (this.invite_method == 'email') return this.email;
      return this.phone;
    }

    // shouldn't get here...
    console.log('comparisonValueFor(), attributeName:', attributeName, '(not supported)');
    return '';
  };

  @computed
  get toJson() {
    return {
      name: this.name,
      phone: this.phone,
      email: this.email,
      invite_method: this.invite_method,
      invited_by: this.invited_by,
      isGuest: this.isGuest,
      isDraft: this.isDraft,
      contact_id: this.contact_id,
      contact_group_ids: this.contact_group_ids,
      guest_id: this.guest_id,
    };
  }
}
